import { useEffect, useState } from 'react'
import Confetti from 'react-confetti'
import { Wheel } from 'react-custom-roulette'
import './style.css'

interface RouletteProps {
  data: any;
  setNewPrizeNumber: any;
  answerCount: number;
  mustSpin: boolean;
  setMustSpin: any;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Prize that has a 1-per-day limit */
const DAILY_LIMITED_PRIZE = 'Totoka Babysec';

// ─── Date helpers ─────────────────────────────────────────────────────────────

const getTodayStr = (): string => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

/** Initial per-slot quantities (matching config.json data_roulette order). */
const initialSlotQuantities: Record<number, number> = {
  0: 1400,  // Sab. Pom Pom
  1: 240,   // Sh. Pom Pom
  2: 1000,  // Lenço Elite
  3: 8,     // Totoka Babysec
  4: 1500,  // Desconto 15%
};

const NUM_SLOTS = 5;
const DEFAULT_ORDER = [0, 1, 2, 3, 4];

// ─── Totokinha daily limit ────────────────────────────────────────────────────

const DAILY_KEY = 'softys_totokinha_daily';

const isTotokinhaBlockedToday = (): boolean => {
  try {
    const stored = localStorage.getItem(DAILY_KEY);
    if (!stored) return false;
    const { date } = JSON.parse(stored);
    return date === getTodayStr();
  } catch {
    return false;
  }
};

const markTotokinhaDrawnToday = () =>
  localStorage.setItem(DAILY_KEY, JSON.stringify({ date: getTodayStr() }));

// ─── Slot-based localStorage ──────────────────────────────────────────────────

const SLOT_QTY_KEY = 'softys_roulette_slot_quantities';
const ORDER_KEY    = 'softys_roulette_order';

const saveSlotQuantities = (quantities: Record<number, number>) => {
  localStorage.setItem(SLOT_QTY_KEY, JSON.stringify(quantities));
};

const loadSlotQuantities = (): Record<number, number> => {
  try {
    const stored = localStorage.getItem(SLOT_QTY_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Ensure all 10 slots exist
      const result: Record<number, number> = {};
      for (let i = 0; i < NUM_SLOTS; i++) {
        result[i] = parsed[String(i)] ?? initialSlotQuantities[i];
      }
      return result;
    }
    return { ...initialSlotQuantities };
  } catch {
    return { ...initialSlotQuantities };
  }
};

const loadOrder = (): number[] => {
  try {
    const stored = localStorage.getItem(ORDER_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length === NUM_SLOTS) return parsed;
    }
    return [...DEFAULT_ORDER];
  } catch {
    return [...DEFAULT_ORDER];
  }
};

export default function Roulette({ data, setNewPrizeNumber, answerCount, mustSpin, setMustSpin }: RouletteProps) {
  const [slotQuantities, setSlotQuantities] = useState<Record<number, number>>(loadSlotQuantities());
  const [allRouletteData, setAllRouletteData] = useState<any[]>([]);
  const [allSlotMapping, setAllSlotMapping] = useState<number[]>([]); // visual index → original slot ID (full, unfiltered)
  const [isFinished, setIsFinished] = useState(false);

  // ── Compute visible data: filter out exhausted slots ────────────────────────
  const visibleData: any[] = [];
  const visibleMapping: number[] = []; // visual index in filtered wheel → original slot ID
  for (let i = 0; i < allRouletteData.length; i++) {
    const slotId = allSlotMapping[i];
    const qty = slotQuantities[slotId] ?? 0;
    const name: string = allRouletteData[i]?.option ?? '';
    const blocked = name === DAILY_LIMITED_PRIZE && isTotokinhaBlockedToday();
    if (qty > 0 && !blocked) {
      visibleData.push(allRouletteData[i]);
      visibleMapping.push(slotId);
    }
  }

  // Decrement a specific slot's quantity
  const updateSlotQuantity = (slotId: number, prizeName: string) => {
    setSlotQuantities((prev) => {
      const updated = { ...prev };
      if ((updated[slotId] ?? 0) > 0) {
        updated[slotId] -= 1;
      }
      if (prizeName === DAILY_LIMITED_PRIZE) {
        markTotokinhaDrawnToday();
      }
      saveSlotQuantities(updated);
      return updated;
    });
  };

  const getValidPrize = () => {
    if (visibleData.length === 0) return 0;

    // Build list of unique prize NAMES that still have stock in at least one visible slot
    const availableNames = new Set<string>();
    for (let i = 0; i < visibleData.length; i++) {
      const slotId = visibleMapping[i];
      const option: string = visibleData[i].option ?? '';
      const qty = slotQuantities[slotId] ?? 0;
      const blocked = option === DAILY_LIMITED_PRIZE && isTotokinhaBlockedToday();
      if (qty > 0 && !blocked) {
        availableNames.add(option);
      }
    }

    // Fallback: if nothing available, return first index
    if (availableNames.size === 0) return 0;

    // Pick a prize name with EQUAL probability per NAME
    const namesArray = Array.from(availableNames);
    const chosenPrize = namesArray[Math.floor(Math.random() * namesArray.length)];

    // Find all visible indices with that name that still have stock
    const matchingIndices: number[] = [];
    for (let i = 0; i < visibleData.length; i++) {
      if (visibleData[i].option === chosenPrize) {
        const slotId = visibleMapping[i];
        if ((slotQuantities[slotId] ?? 0) > 0) {
          matchingIndices.push(i);
        }
      }
    }

    // Return a random matching visual index (for natural wheel landing)
    return matchingIndices.length > 0
      ? matchingIndices[Math.floor(Math.random() * matchingIndices.length)]
      : 0;
  };

  const wheelPrizeIndex = getValidPrize();

  const handleStopSpinning = () => {
    setIsFinished(true);
    setTimeout(() => {
      const slotId = visibleMapping[wheelPrizeIndex];
      const selectedOption = visibleData[wheelPrizeIndex]?.option;

      if (selectedOption) {
        // Emit original slot ID to parent (so parent can index into data_roulette correctly)
        setNewPrizeNumber(slotId);
        updateSlotQuantity(slotId, selectedOption);
      }
      setIsFinished(false);
    }, 4000);    
  };

  useEffect(() => {
    if (data && data.data_roulette) {
      const originalData = data.data_roulette;
      const order = loadOrder();

      // Reorder data according to admin-saved order
      const reordered: any[] = [];
      const mapping: number[] = [];
      for (const slotId of order) {
        if (originalData[slotId]) {
          reordered.push(originalData[slotId]);
          mapping.push(slotId);
        }
      }

      setAllRouletteData(reordered);
      setAllSlotMapping(mapping);

      // Initialize slot quantities if not stored yet
      const stored = localStorage.getItem(SLOT_QTY_KEY);
      if (!stored) {
        saveSlotQuantities(initialSlotQuantities);
        setSlotQuantities({ ...initialSlotQuantities });
      }
    } else {
      console.error("data_roulette não está disponível.");
    }
  }, [data]);


  if (visibleData.length === 0) {
    return (
      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div style={{ color: '#ffffff', fontSize: '28px' }}>Carregando roleta...</div>
      </div>
    );
  }

  try {
    return (
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {isFinished && (
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            numberOfPieces={300}
            initialVelocityX={10}
            initialVelocityY={20}
            gravity={0.4}
            style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999, pointerEvents: 'none' }}
          />
        )}
        <div className="roulette-wheel-wrapper">
        <Wheel
          mustStartSpinning={mustSpin}
          prizeNumber={wheelPrizeIndex}
          spinDuration={0.8}
          data={visibleData.map((item: any) => {
            // Calcula fontSize dinâmico: textos longos ficam menores para caber no setor
            const text: string = item.option || '';
            const len = text.length;
            let itemFontSize: number;
            if (len > 26) itemFontSize = 9;
            else if (len > 20) itemFontSize = 11;
            else itemFontSize = 14;

            return {
              option: text,
              image: item.image
                ? { uri: item.image, sizeMultiplier: 0.3, offsetX: 0, offsetY: 0, landscape: true }
                : undefined,
              style: {
                backgroundColor: item.style?.backgroundColor,
                textColor: item.style?.textColor,
                fontSize: itemFontSize,
              },
            };
          })}
          onStopSpinning={handleStopSpinning}
          outerBorderColor={'#FFFFFF'}
          outerBorderWidth={12}
          innerBorderColor={'#FFFFFF'}
          innerBorderWidth={1}
          radiusLineColor={'#FFFFFF'}
          radiusLineWidth={6}
          backgroundColors={visibleData.map((item: any) => item.style?.backgroundColor)}
          textColors={visibleData.map((item: any) => item.style?.textColor)}
          fontSize={14}
          textDistance={55}
          fontFamily="Arial"
        />
        </div>
      </div>
    );
  } catch (error) {
    return (
      <div style={{ width: '100%', textAlign: 'center', color: 'white', fontSize: '24px', padding: '40px' }}>
        FALHA! RECARREGUE A PÁGINA!
      </div>
    );
  }
}
